
import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppStore } from './store/useAppStore';
import { Layout } from './components/Layout';
import { supabase } from './lib/supabase';
import { Loader2 } from 'lucide-react';

// Page components
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import LibraryPage from './pages/LibraryPage';
import PracticePage from './pages/PracticePage';
import HistoryPage from './pages/HistoryPage';
import FlashcardsPage from './pages/FlashcardsPage';

const LoadingOverlay = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6 text-center">
    <div className="relative mb-6">
      <Loader2 className="h-12 w-12 text-primary-500 animate-spin" />
      <div className="absolute inset-0 bg-primary-500/10 blur-xl rounded-full animate-pulse" />
    </div>
    <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em]">Initializing Session</p>
    <p className="mt-4 text-slate-300 text-[9px] font-bold uppercase tracking-widest">Checking local cache...</p>
  </div>
);

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-slate-900 mb-2">404</h1>
      <p className="text-slate-600">Page not found</p>
    </div>
  </div>
);

const App: React.FC = () => {
  const setUser = useAppStore(state => state.setUser);
  const setAuthInitialized = useAppStore(state => state.setAuthInitialized);
  const isAuthInitialized = useAppStore(state => state.isAuthInitialized);

  useEffect(() => {
    let timeoutId: number;

    const initializeAuth = async () => {
      // If we're already known to be offline, skip the timeout and just mark as ready
      if (!navigator.onLine) {
        console.log('App is offline. Skipping network auth check.');
        setAuthInitialized(true);
        return;
      }

      // Very aggressive 1.5s timeout for the auth check when online
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Auth timeout')), 1500);
      });

      try {
        const sessionPromise = supabase.auth.getSession();
        const result: any = await Promise.race([sessionPromise, timeoutPromise]);
        const session = result?.data?.session;

        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Member',
            created_at: session.user.created_at
          });
        }
      } catch (err) {
        console.warn('Network auth failed or timed out. Continuing in Guest/Offline mode.');
      } finally {
        window.clearTimeout(timeoutId);
        setAuthInitialized(true);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'Member',
          created_at: session.user.created_at
        });
      } else {
        setUser(null);
      }
      setAuthInitialized(true);
    });

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timeoutId);
    };
  }, [setUser, setAuthInitialized]);

  if (!isAuthInitialized) return <LoadingOverlay />;

  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<AuthPage type="login" />} />
        <Route path="/signup" element={<AuthPage type="signup" />} />

        {/* Routes */}
        <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />
        <Route path="/upload" element={<Layout><UploadPage /></Layout>} />
        <Route path="/library" element={<Layout><LibraryPage /></Layout>} />
        <Route path="/flashcards" element={<Layout><FlashcardsPage /></Layout>} />
        <Route path="/flashcards/:pdfId" element={<Layout><FlashcardsPage /></Layout>} />
        <Route path="/practice" element={<Layout><PracticePage /></Layout>} />
        <Route path="/practice/:pdfId" element={<Layout><PracticePage /></Layout>} />
        <Route path="/history" element={<Layout><HistoryPage /></Layout>} />
        <Route path="/settings" element={<Layout><div className="text-slate-600 p-8">Settings Page</div></Layout>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </HashRouter>
  );
};

export default App;
